import React, { useEffect, useState } from "react";

const MAX_FILES_DEFAULT = 10;

interface Slot {
  id: number;
  file: File | null;
}

interface Props {
  label: string;
  name: string;
  description?: string;
  error?: string;
  accept?: string;
  maxFiles?: number;
}

export default function FileUploads(props: Props) {
  const {
    label,
    name,
    description,
    error,
    accept = ".pdf,.doc,.docx,.jpg,.jpeg,.jpe,.tif,.tiff,.gif,.png,.mp3,.wav",
    maxFiles = MAX_FILES_DEFAULT
  } = props;

  const [slots, setSlots] = useState<Slot[]>([{ id: 0, file: null }]);
  const hasFiles = slots.some((s) => s.file !== null);

  const handleFileChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0] ?? null;
    setSlots((prev) => {
      const next = prev.map((slot, i) =>
        i === index ? { ...slot, file } : slot
      );
      const allFilled = next.every((s) => s.file !== null);
      if (allFilled && next.length < maxFiles) {
        return [...next, { id: Date.now(), file: null }];
      }
      return next;
    });
  };

  const removeSlot = (index: number) => {
    setSlots((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    const form = document.querySelector(".mitt-frie-ord-form-fields");
    if (!form) return;
    form.dispatchEvent(
      new CustomEvent("fileuploadchange", {
        detail: { hasFiles },
        bubbles: true
      })
    );
  }, [hasFiles]);

  return (
    <div
      className="field field-file-uploads"
      data-file-uploads
      data-has-files={hasFiles ? "true" : "false"}>
      <label htmlFor={`file-upload-${slots[0]?.id ?? 0}`}>{label}</label>
      {description && <p className="field-hint">{description}</p>}
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      <div className="file-list" aria-live="polite">
        {slots.map((slot, index) => (
          <div key={slot.id} className="file-list__item">
            <input
              id={index === 0 ? `file-upload-${slot.id}` : undefined}
              type="file"
              name={name}
              accept={accept}
              onChange={(e) => handleFileChange(index, e)}
              aria-label={index === 0 ? label : `${label} ${index + 1}`}
            />
            {slot.file && (
              <button
                type="button"
                className="file-list__remove"
                onClick={() => removeSlot(index)}
                aria-label={`Fjern fil ${slot.file?.name ?? ""}`}>
                Fjern fil
              </button>
            )}
          </div>
        ))}
      </div>
      <p className="file-count" aria-live="polite">
        {slots.filter((s) => s.file).length} / {maxFiles} filer
      </p>
    </div>
  );
}
